<template>
  <div>
    <!-- Grafo de la regla -->
    <div
      class="graph-box pa-4 rounded-lg d-flex align-center justify-center"
      :style="{ background: effectiveTint }"
    >
      <a-pricing-strategy-rule-graph
        :key="graphKey"
        :rule="graphRule"
        :color="effectiveColor"
        :evaluation="suggestionEvaluation"
      />
    </div>

    <!-- Medida tomada (solo cuando viene de una sugerencia) -->
    <template v-if="hasMeasure">
      <div
        class="text-caption font-weight-bold text-uppercase blue-grey--text text--lighten-2 mt-4 mb-1"
      >
        {{ $t("measure") }}
      </div>
      <v-card outlined class="detail-box py-2 px-3">
        <div class="text-body-2 font-weight-medium">{{ measureText }}</div>
        <div v-if="measureSpot" class="text-caption grey--text">
          {{ measureSpot }}
        </div>
      </v-card>
    </template>

    <!-- Cuándo se aplica -->
    <div
      class="text-caption font-weight-bold text-uppercase blue-grey--text text--lighten-2 mt-4 mb-1"
    >
      {{ $t("rule_templates_when_applies") }}
    </div>
    <template v-for="(route, ri) in routes">
      <!-- Separador OR entre rutas -->
      <div v-if="ri > 0" :key="`or-${ri}`" class="d-flex align-center my-1">
        <v-divider />
        <span
          class="text-caption font-weight-bold blue-grey--text text--lighten-2 mx-2"
        >
          {{ $t("or") }}
        </span>
        <v-divider />
      </div>
      <v-card
        :key="`route-${ri}`"
        outlined
        class="detail-box py-2 px-3"
        :class="routeClass(ri)"
      >
        <!-- Cuando la regla viene de una sugerencia, se dice de cada ruta si
             disparó, si se descartó o si ni se llegó a mirar -->
        <div
          v-if="routeStatus(ri)"
          class="d-flex align-center text-caption font-weight-bold mb-1"
          :class="routeStatus(ri).textClass"
          style="gap: 4px"
        >
          <v-icon size="14" :color="routeStatus(ri).color">
            {{ routeStatus(ri).icon }}
          </v-icon>
          {{ routeStatus(ri).label }}
        </div>

        <div
          v-for="(cond, ci) in route.conditions"
          :key="`cond-${ri}-${ci}`"
          class="text-body-2"
        >
          <span v-if="ci > 0" class="font-weight-bold">{{ $t("and") }}</span>
          {{ conditionText(cond) }}
          <!-- Valor real medido contra el umbral: es lo que deja ver por qué
               esta ruta encajó o no -->
          <span
            v-if="conditionCheck(ri, ci)"
            class="text-caption font-weight-medium ml-1"
            :class="conditionCheck(ri, ci).textClass"
          >
            {{ conditionCheck(ri, ci).text }}
          </span>
        </div>
        <!-- Con valor por ruta la acción va aquí, pegada a las condiciones
             que la disparan -->
        <div
          v-if="perRouteAction"
          class="text-body-2 font-weight-medium primary--text mt-1"
        >
          → {{ routeActionText(route) }}
        </div>
      </v-card>
    </template>

    <!-- Acción única -->
    <template v-if="!perRouteAction">
      <div
        class="text-caption font-weight-bold text-uppercase blue-grey--text text--lighten-2 mt-4 mb-1"
      >
        {{ $t("rule_templates_action") }}
      </div>
      <v-card outlined class="detail-box py-2 px-3 text-body-2">
        {{ actionText }}
      </v-card>
    </template>

    <!-- Frecuencia -->
    <div
      class="text-caption font-weight-bold text-uppercase blue-grey--text text--lighten-2 mt-4 mb-1"
    >
      {{ $t("rule_templates_frequency") }}
    </div>
    <div class="text-body-2">{{ frequencyText }}</div>
  </div>
</template>

<script>
import aPricingStrategyRuleGraph from "@/components/pricing-strategy/aPricingStrategyRuleGraph.vue";
import {
  actionPhrase,
  hasPerRouteAction,
  routeActionPhrase,
  alphaColor,
  conditionPhrase,
  conditionMeasureValue,
  frequencyPhrase,
  measurePhrase,
  suggestionSpotLiteral
} from "@/components/pricing-strategy/pricingStrategyUtils";
import * as Utils from "@/utils.js";

export default {
  name: "a-pricing-strategy-rule-detail",
  components: {
    aPricingStrategyRuleGraph
  },
  props: {
    rule: {
      type: Object,
      required: true
    },
    // Color del grafo; por defecto el color de la regla intensificado
    graphColor: {
      type: String,
      default: null
    },
    // Tinte del fondo del grafo; por defecto derivado del color
    graphTint: {
      type: String,
      default: null
    },
    // Sugerencia asociada (opcional): añade la sección "Medida"
    suggestion: {
      type: Object,
      default: null
    }
  },
  computed: {
    routes() {
      return (this.rule && this.rule.routes) || [];
    },
    effectiveColor() {
      if (this.graphColor) return this.graphColor;
      return this.rule && this.rule.color
        ? Utils.intensifyColor(this.rule.color)
        : "#666666";
    },
    effectiveTint() {
      return this.graphTint || alphaColor(this.effectiveColor, 0.12);
    },
    // Clave para remontar el grafo cuando cambia la regla (recalcula el ancho)
    graphKey() {
      return (this.rule && this.rule.id) || this.routes.length;
    },
    // Objeto en la forma que consume aPricingStrategyRuleGraph
    graphRule() {
      const r = this.rule || {};
      const a = r.action || {};
      return {
        name: r.name || "",
        routes: r.routes || [],
        schedule: r.schedule,
        action: a
      };
    },
    perRouteAction() {
      return hasPerRouteAction(this.rule);
    },
    actionText() {
      return actionPhrase(this, this.rule.action);
    },
    frequencyText() {
      return frequencyPhrase(this, this.rule.schedule);
    },
    hasMeasure() {
      return !!(this.suggestion && this.suggestion.measure);
    },
    measureText() {
      if (!this.hasMeasure) return "";
      return measurePhrase(this, this.suggestion);
    },
    measureSpot() {
      if (!this.hasMeasure) return "";
      return suggestionSpotLiteral(this, this.suggestion);
    },
    suggestionEvaluation() {
      return (this.suggestion && this.suggestion.evaluation) || null;
    },
    // Rutas evaluadas, indexadas por su posición en la regla. Vacío cuando la
    // sugerencia es anterior al campo `evaluation`: entonces no se pinta nada,
    // que es mejor que pintar suposiciones.
    evaluatedRoutes() {
      const evaluation = this.suggestionEvaluation;
      const routes = (evaluation && evaluation.routes) || [];
      return routes.reduce((acc, route) => {
        acc[route.index] = route;
        return acc;
      }, {});
    }
  },
  methods: {
    conditionText(condition) {
      return conditionPhrase(this, condition);
    },
    routeActionText(route) {
      return routeActionPhrase(this, this.rule, route);
    },
    // Cómo le fue a una ruta: disparó, se descartó, o no se llegó a evaluar
    // porque una anterior ya había disparado.
    routeStatus(routeIndex) {
      const route = this.evaluatedRoutes[routeIndex];
      if (!route) return null;

      if (route.met) {
        return {
          label: this.$t("route_triggered"),
          icon: "mdi-check-circle-outline",
          color: "success",
          textClass: "success--text"
        };
      }
      if (!route.evaluated) {
        return {
          label: this.$t("route_not_evaluated"),
          icon: "mdi-minus-circle-outline",
          color: "grey",
          textClass: "grey--text"
        };
      }
      return {
        label: this.$t("route_discarded"),
        icon: "mdi-close-circle-outline",
        color: "grey darken-1",
        textClass: "grey--text text--darken-1"
      };
    },
    routeClass(routeIndex) {
      const route = this.evaluatedRoutes[routeIndex];
      if (!route) return "";
      if (route.met) return "route-triggered";
      return route.evaluated ? "route-discarded" : "route-skipped";
    },
    // "89% vs 93%-96%", "6 vs ≥3", "1507€ vs >1499€": el valor medido al lado
    // del umbral. Sin valor medido no se pinta: una ruta que no se evaluó no
    // tiene nada que enseñar.
    conditionCheck(routeIndex, conditionIndex) {
      const route = this.evaluatedRoutes[routeIndex];
      const condition =
        route && route.conditions && route.conditions[conditionIndex];
      if (
        !condition ||
        condition.actual === null ||
        condition.actual === undefined
      ) {
        return null;
      }

      // Sólo el valor medido: el umbral ya lo dice la frase de la condición,
      // que va justo al lado, y repetirlo hacía la línea ilegible.
      return {
        text: `${this.$t("measure")}: ${conditionMeasureValue(
          this,
          condition
        )}`,
        textClass: condition.met ? "success--text" : "red--text text--lighten-1"
      };
    }
  }
};
</script>

<style scoped lang="scss">
.graph-box {
  min-height: 90px;
}
.detail-box {
  background: #f7f9fb;
}
// La ruta que disparó se lee de un vistazo; las descartadas siguen visibles
// porque saber qué NO se cumplió es parte de la justificación.
.route-triggered {
  border-color: var(--v-success-base, #4caf50) !important;
  border-width: 2px !important;
  background: #f3faf4;
}
.route-discarded,
.route-skipped {
  opacity: 0.7;
}
</style>
